import { StanButton, StanProgressBar } from '@/components';
import { SubscriptionPriceCard } from '@/features/subscriptions/components/SubscriptionPriceCard';
import { PromoCodeInput } from '@/features/subscriptions/components/PromoCodeInput';
import { usePlanSelection, useSubscriptionFlags } from '@/features/subscriptions/hooks';
import { getProgressPercentage } from '@/features/subscriptions/utils/subscriptionFlags';
import { StanLoader } from '@/components/core-components/StanLoader/StanLoader';
import { useStanRouter } from '@/hooks/useStanRouter';
import IcCheck from '@/assets/icons/ic-check.svg';

const SubscribePlanPage = () => {
    const { push } = useStanRouter();
    const flags = useSubscriptionFlags();
    const {
        isLoading,
        selectedInterval,
        handleIntervalChange,
        onSubmit,
        showPromo,
        discountCode,
        handlePromoApplied,
        discountPercent,
        showMonthly,
        showAnnual,
        currency,
        monthlyPrice,
        annualPrice,
        originalMonthlyPrice,
        originalAnnualPrice,
        monthlyAnnualizedPrice,
        hasPastSubscription,
        trialDays,
    } = usePlanSelection();

    const progressPercentage = getProgressPercentage('plan', !!flags.add_socials_before_subscription);

    if (isLoading) {
        return (
            <div className='flex min-h-svh w-full items-center justify-center'>
                <StanLoader
                    className='flex items-center'
                    animationDuration={1000}
                    dotSize={18}
                    dotsNum={3}
                    color='var(--bg-color-primary-brand-main)'
                />
            </div>
        );
    }

    return (
        <div className='flex min-h-[calc(100svh-4rem)] w-full max-w-sm flex-col'>
            {/* Progress Bar */}
            <div className='mx-auto w-1/2 pb-7'>
                <StanProgressBar
                    value={progressPercentage}
                    min={0}
                    max={100}
                    progressColor='#a286ff'
                    barColor='#eef1f5'
                />
            </div>

            {/* Main Container */}
            <div className='mx-auto flex w-full flex-1 flex-col px-8'>
                {/* Title */}
                <div className='mx-auto px-5 text-center'>
                    <h2 className='text-dark'>
                        Pick your plan, start
                        <br />
                        earning today 💸
                    </h2>
                </div>

                {/* Description */}
                <div className='mx-auto py-4.5 text-center'>
                    <div className='flex flex-col gap-1'>
                        <div className='flex items-center justify-center gap-1'>
                            <IcCheck className='size-6' />
                            <span className='text-body-sm text-dark'>
                                Save <span className='font-bold'>$100s</span> by choosing Stan
                            </span>
                        </div>
                        <div className='flex items-center justify-center gap-1'>
                            <IcCheck className='size-6' />
                            <span className='text-body-sm text-dark'>All-In-One store, easy to set up</span>
                        </div>
                    </div>
                </div>

                {/* Subscription Options */}
                <div className='mt-5 flex flex-col gap-5'>
                    {showMonthly && (
                        <SubscriptionPriceCard
                            id='monthly_subscription'
                            name='Monthly'
                            selected={selectedInterval === 'monthly'}
                            price={monthlyPrice}
                            originalPrice={originalMonthlyPrice}
                            currency={currency}
                            interval='month'
                            trialDays={trialDays}
                            hasPastSubscription={hasPastSubscription}
                            onSelect={() => handleIntervalChange('monthly')}
                        />
                    )}

                    {showAnnual && (
                        <SubscriptionPriceCard
                            id='annual_subscription'
                            name='Yearly'
                            selected={selectedInterval === 'annual'}
                            price={annualPrice}
                            originalPrice={originalAnnualPrice ?? monthlyAnnualizedPrice}
                            currency={currency}
                            interval='year'
                            trialDays={trialDays}
                            hasPastSubscription={hasPastSubscription}
                            savingsBadge={!discountPercent ? 'Save 20%' : undefined}
                            onSelect={() => handleIntervalChange('annual')}
                        />
                    )}

                    {/* Promo Code Input - Show when promo flag is set OR when there's a discount code */}
                    {(showPromo || discountCode) && (
                        <PromoCodeInput prefillCode={discountCode} onPromoApplied={handlePromoApplied} />
                    )}

                    {/* Due Today Note */}
                    {!hasPastSubscription && (
                        <p className='text-body-sm text-dark flex w-full justify-center font-medium'>Due today $0 🙌</p>
                    )}
                </div>

                {/* Spacer */}
                <div className='flex-1' />

                {/* Submit Button */}
                <div className='mt-auto pt-8'>
                    <StanButton
                        name='subscription-option-submit-button'
                        type='button'
                        color='primary'
                        onClick={onSubmit}
                        className='w-full'
                    >
                        Next
                    </StanButton>

                    {hasPastSubscription && (
                        <div className='mt-6 w-full'>
                            <StanButton
                                name='subscription-option-dashboard-button'
                                type='button'
                                variant='link'
                                size='sm'
                                onClick={() => push('/dashboard')}
                                className='mx-auto! mt-2'
                            >
                                Dashboard
                            </StanButton>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SubscribePlanPage;
